import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, API, fmtMoney, fmtPct } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Printer,
  Share2,
  Link as LinkIcon,
  Copy,
  Check,
  MapPin,
  Home,
  Bed,
  Bath,
  Ruler,
  AlertTriangle,
  ListChecks,
  Building2,
  TrendingUp,
  Camera,
  Trash2,
  Plus,
  X,
  Download,
} from "lucide-react";
import { ReportBody } from "@/components/ReportBody";
import PropertyPhotosCard from "@/components/PropertyPhotosCard";
import BuyerLinkPicker from "@/components/BuyerLinkPicker";
import MatchedBuyersPanel from "@/components/MatchedBuyersPanel";
import BuyBoxMatchesPanel from "@/components/BuyBoxMatchesPanel";
import LeadAppointmentsPanel from "@/components/LeadAppointmentsPanel";
import StageAuditPanel from "@/components/StageAuditPanel";
import LeadOutboundQueuePanel from "@/components/LeadOutboundQueuePanel";
import HistoryStrip from "@/components/HistoryStrip";
import DispoTimeline from "@/components/DispoTimeline";
import SmsThread from "@/components/SmsThread";
import CallLogPanel from "@/components/CallLogPanel";
import MergeHistoryPanel from "@/components/MergeHistoryPanel";
import ContractPreviewButton from "@/components/ContractPreviewButton";
import CallRecordingsList from "@/components/CallRecordingsList";
import AIScorePanel from "@/components/AIScorePanel";
import AIValuationPanel from "@/components/AIValuationPanel";
import { Mic } from "lucide-react";

export default function LeadReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // Magic onboarding: when arriving via /register → /app/leads/:id/report?autogen=1
  // the page kicks off report generation automatically and shows a hero
  // "Building your first analysis…" state in place of the empty-report fallback.
  const autogenRef = useRef(false);
  const [autogen, setAutogen] = useState(searchParams.get("autogen") === "1");
  const [copied, setCopied] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapName, setSnapName] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);
  const [snapPanel, setSnapPanel] = useState(false);
  const [snapCopied, setSnapCopied] = useState(null);

  const downloadPdf = useCallback(async () => {
    // Auth-aware download: fetch the PDF as a blob (sends the JWT via the
    // shared axios instance) → build an object URL → click a hidden anchor.
    try {
      const r = await api.get(`/leads/${id}/report.pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeAddr = (data?.lead?.property_address || "property")
        .replace(/[,]/g, "").replace(/\s+/g, "-").slice(0, 60);
      a.href = url;
      a.download = `${safeAddr}-analysis.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Global toast is already mounted — silent fail is fine.
    }
  }, [id, data]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, snaps, eng] = await Promise.all([
        api.get(`/leads/${id}/report`),
        api.get(`/leads/${id}/snapshots`).then((r) => r.data).catch(() => []),
        api.get(`/leads/${id}/photo-engagement`).then((r) => r.data).catch(() => null),
      ]);
      setData({ ...data, photo_engagement: eng });
      setSnapshots(snaps);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Magic onboarding: auto-fire the generator exactly once when ?autogen=1
  // is present. If RentCast didn't have data for this address (no ARV), we
  // bail out of the autogen path and show a guided "Tell us about this
  // property" form instead — gracefully degrading from magic to manual.
  useEffect(() => {
    if (!autogen || autogenRef.current || loading || !data) return;
    const hasReport = !!data?.report?.metrics;
    const lead = data?.lead;
    const hasArv = !!(lead?.estimated_arv && lead.estimated_arv > 0);
    if (hasReport) {
      // Already generated (e.g. user refreshed). Just drop the flag.
      setAutogen(false);
      searchParams.delete("autogen");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!hasArv) {
      // RentCast miss — leave autogen flag in URL so the manual form renders.
      // Don't fire generation yet; user has to fill in ARV first.
      return;
    }
    autogenRef.current = true;
    (async () => {
      setGenerating(true);
      try {
        await api.post(`/leads/${id}/report/generate`);
        // eslint-disable-next-line no-import-assign
        const { track } = await import("@/lib/analytics");
        track("magic_analysis_ready");
        await load();
      } finally {
        setGenerating(false);
        setAutogen(false);
        searchParams.delete("autogen");
        setSearchParams(searchParams, { replace: true });
      }
    })();
  }, [autogen, loading, data, id, load, searchParams, setSearchParams]);

  // Called when the user submits the manual property-details form to recover
  // from a RentCast miss. PATCHes the lead with their numbers, then triggers
  // the standard generation flow.
  const submitManualDetails = async (fields) => {
    autogenRef.current = true;
    setGenerating(true);
    try {
      await api.patch(`/leads/${id}`, fields);
      // Best-effort mark that this lead recovered via the manual form so
      // admin analytics can distinguish abandons from rescues. Don't block
      // on failure — it's just observability.
      api.post(`/onboarding/magic-lead/${id}/resolved-via-manual`).catch(() => {});
      await api.post(`/leads/${id}/report/generate`);
      await load();
    } finally {
      setGenerating(false);
      setAutogen(false);
      searchParams.delete("autogen");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/leads/${id}/report/generate`);
      // refresh full payload (lead + share data)
      await load();
    } catch {
      // toast is shown globally for API failures
    } finally {
      setGenerating(false);
    }
  };

  const toggleShare = async () => {
    if (!data) return;
    const enabled = !data.share_enabled;
    await api.patch(`/leads/${id}/report/share`, { enabled });
    setData((d) => ({ ...d, share_enabled: enabled }));
  };

  const publicUrl = data?.share_token
    ? `${window.location.origin}/r/${data.share_token}`
    : "";

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const saveSnapshot = async () => {
    setSnapBusy(true);
    try {
      await api.post(`/leads/${id}/snapshots`, { name: snapName });
      setSnapName("");
      setSnapPanel(true);
      const snaps = await api.get(`/leads/${id}/snapshots`).then((r) => r.data);
      setSnapshots(snaps);
    } finally {
      setSnapBusy(false);
    }
  };

  const deleteSnapshot = async (snapId) => {
    if (!window.confirm("Delete this snapshot? The public link will stop working.")) return;
    await api.delete(`/leads/${id}/snapshots/${snapId}`);
    setSnapshots((arr) => arr.filter((s) => s.id !== snapId));
  };

  const copySnapshotLink = async (snap) => {
    const url = `${window.location.origin}/s/${snap.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setSnapCopied(snap.id);
      setTimeout(() => setSnapCopied(null), 1800);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="card text-center py-16 text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading report…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="empty">
        <div className="font-semibold mb-1">Report not found</div>
        <Link to="/app/leads" className="btn-outline mt-3 inline-flex">Back to leads</Link>
      </div>
    );
  }

  const { lead, report, share_token, share_enabled } = data;
  const hasReport = !!report;

  return (
    <div data-testid="lead-report-page">
      {/* Toolbar (hidden in print) */}
      <div className="print:hidden flex flex-wrap items-center justify-between gap-3 mb-5">
        <button type="button" className="btn-ghost" onClick={() => nav("/app/leads")} data-testid="report-back">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {hasReport && (
            <>
              <button type="button" className="btn-outline" onClick={() => setSnapPanel((p) => !p)} data-testid="report-snapshots-toggle">
                <Camera size={14} /> Snapshots {snapshots.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[var(--ink)] text-white text-[10px] font-bold tabular-nums">{snapshots.length}</span>}
              </button>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--line)] bg-white text-xs">
                <input type="checkbox" checked={!!share_enabled} onChange={toggleShare} data-testid="report-share-toggle" />
                <span className="font-semibold text-[var(--ink-2)]">Share link {share_enabled ? "ON" : "OFF"}</span>
              </div>
              {share_enabled && share_token && (
                <button type="button" className="btn-outline" onClick={copyLink} data-testid="report-copy-link">
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy public link"}
                </button>
              )}
              <button type="button" className="btn-outline" onClick={() => window.print()} data-testid="report-print">
                <Printer size={14} /> Print
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={downloadPdf}
                title="Download branded PDF (shareable with buyers + JV partners)"
                data-testid="report-download-pdf"
              >
                <Download size={14} /> Download PDF
              </button>
            </>
          )}
          <button type="button" className="btn-money" onClick={generate} disabled={generating} data-testid="report-generate">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {generating ? "Generating…" : hasReport ? "Regenerate" : "Generate Report"}
          </button>
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(`Move this lead to Trash?\n\n${lead.property_address || "Lead"} will be restorable from Leads → Trash for 30 days.`)) return;
              try {
                await api.delete(`/leads/${id}`);
                toast.success("Moved to Trash");
                nav("/app/leads");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Delete failed");
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-rose-200 text-rose-600 hover:bg-rose-50 transition"
            title="Move this lead to Trash (restorable for 30 days)"
            data-testid="report-delete-lead"
          >
            <Trash2 size={14} /> Delete lead
          </button>
        </div>
      </div>

      {/* SMS thread + tap-to-call — always visible regardless of report state. */}
      <SmsThread lead={lead} />
      <AIScorePanel
        lead={lead}
        onUpdate={(patch) => setData((d) => ({ ...d, lead: { ...d.lead, ...patch } }))}
      />
      <AIValuationPanel
        lead={lead}
        onUpdate={(patch) => setData((d) => ({ ...d, lead: { ...d.lead, ...patch } }))}
      />
      <CallLogPanel leadId={id} />
      <div className="card mb-5" data-testid="lead-recordings">
        <div className="flex items-center gap-2 mb-3">
          <Mic size={14} className="text-indigo-600" />
          <div className="font-display font-bold text-lg">Call recordings</div>
        </div>
        <CallRecordingsList leadId={id} />
      </div>
      <ContractPreviewButton leadId={id} />
      <MergeHistoryPanel leadId={id} />

      {/* Appointments live outside the report-gate — every lead can have
          appointments scheduled even before an AI report is generated. */}
      <LeadAppointmentsPanel leadId={id} />
      <LeadOutboundQueuePanel leadId={id} />
      <StageAuditPanel leadId={id} />
      {/* Buy-box matches + buyer notifications surface on every lead,
          regardless of whether the AI report has been generated yet —
          this is where the "N notified" badge lives after Community
          Layer 3 fires Resend/Twilio match alerts. */}
      <MatchedBuyersPanel leadId={id} />
      <BuyBoxMatchesPanel leadId={id} />

      {!hasReport ? (
        autogen && (!lead?.estimated_arv || lead.estimated_arv === 0) && !generating ? (
          <ManualDetailsForm lead={lead} onSubmit={submitManualDetails} />
        ) : autogen || generating ? (
          <FirstAnalysisLoader address={lead.property_address} />
        ) : (
          <div className="empty">
            <Sparkles size={28} className="mx-auto mb-3 text-[var(--primary)]" />
            <div className="font-display font-bold text-xl mb-1">No report yet for this property</div>
            <p className="text-sm max-w-md mx-auto mb-5">
              Click <strong>Generate Report</strong> to run all 4 strategies, pull AI comps, and produce a
              shareable / printable analysis you can send to cash buyers or sellers.
            </p>
            <button type="button" className="btn-money inline-flex" onClick={generate} disabled={generating}>
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {generating ? "Generating…" : "Generate Report"}
            </button>
          </div>
        )
      ) : (
        <>
          {snapPanel && (
            <SnapshotsPanel
              snapshots={snapshots}
              snapName={snapName}
              setSnapName={setSnapName}
              onSave={saveSnapshot}
              busy={snapBusy}
              onCopy={copySnapshotLink}
              onDelete={deleteSnapshot}
              copiedId={snapCopied}
              onClose={() => setSnapPanel(false)}
            />
          )}
          <HistoryStrip leadId={id} refreshKey={snapshots.length} />
          <DispoTimeline leadId={id} />
          <BuyerLinkPicker leadId={id} shareToken={share_token} shareEnabled={share_enabled} />
          <PropertyPhotosCard
            leadId={lead.id}
            authToken={typeof window !== "undefined" ? localStorage.getItem("af_token") : null}
            onChange={() => load()}
          />
          <ReportBody
            lead={lead}
            report={report}
            engagement={data?.photo_engagement}
            photoEndpoint={`/leads/${lead.id}/photos`}
            authToken={typeof window !== "undefined" ? localStorage.getItem("af_token") : null}
          />
        </>
      )}
    </div>
  );
}

function SnapshotsPanel({ snapshots, snapName, setSnapName, onSave, busy, onCopy, onDelete, copiedId, onClose }) {
  return (
    <div className="print:hidden card mb-5" data-testid="snapshots-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><Camera size={11} /> Frozen versions</div>
          <h3 className="font-display font-bold text-lg mt-1">Report Snapshots</h3>
          <p className="text-xs text-[var(--ink-2)] mt-1">Save a frozen copy with its own public link. Recipients keep seeing the exact version you sent — even if you regenerate later.</p>
        </div>
        <button type="button" className="btn-ghost" onClick={onClose}><X size={14} /></button>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          className="input flex-1"
          placeholder="Snapshot name (optional, e.g. 'Sent to Maya 5/24')"
          value={snapName}
          onChange={(e) => setSnapName(e.target.value)}
          data-testid="snapshot-name-input"
        />
        <button type="button" className="btn-money" onClick={onSave} disabled={busy} data-testid="snapshot-save-button">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {busy ? "Saving" : "Save Snapshot"}
        </button>
      </div>

      {snapshots.length === 0 ? (
        <div className="text-sm text-[var(--ink-2)] italic">No snapshots yet — save your first one above.</div>
      ) : (
        <ul className="space-y-2">
          {snapshots.map((s) => (
            <li key={s.id} className="card-flat p-3 flex items-center justify-between gap-3" data-testid={`snapshot-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{s.name || "(unnamed snapshot)"}</div>
                <div className="text-xs text-[var(--ink-2)] font-mono">{new Date(s.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" className="btn-outline" onClick={() => onCopy(s)} data-testid={`snapshot-copy-${s.id}`}>
                  {copiedId === s.id ? <Check size={13} /> : <Copy size={13} />} {copiedId === s.id ? "Copied" : "Copy link"}
                </button>
                <a href={`/s/${s.share_token}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`snapshot-open-${s.id}`}>
                  <LinkIcon size={13} /> Open
                </a>
                <button type="button" className="btn-ghost" onClick={() => onDelete(s.id)} data-testid={`snapshot-delete-${s.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ── First-time analysis hero loader ─────────────────────────────────────
function FirstAnalysisLoader({ address }) {
  const STEPS = [
    { label: "Geocoding the property", ms: 700 },
    { label: "Pulling Street View + aerial map", ms: 1800 },
    { label: "Estimating ARV and repair scope", ms: 2400 },
    { label: "Finding nearby sales comps", ms: 4500 },
    { label: "Running the 70% Rule and exit strategies", ms: 1500 },
    { label: "Building your branded report", ms: 1100 },
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = [];
    let elapsed = 0;
    STEPS.forEach((s, i) => {
      elapsed += s.ms;
      timers.push(setTimeout(() => setStep(i + 1), elapsed));
    });
    return () => timers.forEach(clearTimeout);
  }, []);
  return (
    <div className="card text-center max-w-2xl mx-auto" data-testid="first-analysis-loader">
      <div className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-4"
           style={{ background: "linear-gradient(135deg,#10B981,#047857)" }}>
        <Sparkles size={26} className="text-white animate-pulse" />
      </div>
      <h2 className="font-display font-extrabold text-2xl mb-1">
        Analyzing {address || "your property"}…
      </h2>
      <p className="text-sm text-[var(--ink-2)] mb-6">
        Hang tight — this takes 10–20 seconds. We&apos;re only doing it once.
      </p>
      <ul className="text-left max-w-md mx-auto space-y-2">
        {STEPS.map((s, i) => {
          const done = step > i;
          const active = step === i;
          return (
            <li key={s.label} className="flex items-center gap-3 text-sm">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0
                ${done ? "bg-emerald-500 text-white" : active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}>
                {done ? <Check size={12} /> : active ? <Loader2 size={11} className="animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />}
              </span>
              <span className={done ? "text-[var(--ink-2)] line-through" : active ? "font-semibold text-[var(--ink)]" : "text-[var(--ink-3)]"}>
                {s.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}




// ── Manual property details fallback (RentCast missed this address) ─────
function ManualDetailsForm({ lead, onSubmit }) {
  const [arv, setArv] = useState("");
  const [repairs, setRepairs] = useState("");
  const [asking, setAsking] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    const arvN = Number(arv.replace(/[^0-9.]/g, ""));
    const repN = Number(repairs.replace(/[^0-9.]/g, ""));
    const askN = Number(asking.replace(/[^0-9.]/g, ""));
    if (!arvN || arvN < 1000) return;
    setSubmitting(true);
    try {
      await onSubmit({
        estimated_arv: arvN,
        estimated_repairs: repN || 0,
        asking_price: askN || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="card max-w-2xl mx-auto" onSubmit={handle} data-testid="manual-details-form">
      <div className="inline-flex w-12 h-12 rounded-full items-center justify-center mb-4"
           style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }}>
        <AlertTriangle size={22} className="text-white" />
      </div>
      <h2 className="font-display font-extrabold text-2xl mb-1">
        We need a couple more numbers
      </h2>
      <p className="text-sm text-[var(--ink-2)] mb-5">
        Our data partner didn&apos;t have a record for <strong>{lead?.property_address}</strong> — this
        happens with new construction, rural addresses, and freshly subdivided lots. Plug in your numbers
        below and we&apos;ll run the full analysis in ~15 seconds.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">
            After-Repair Value (ARV) *
          </label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]">$</span>
            <input
              type="text"
              inputMode="numeric"
              value={arv}
              onChange={(e) => setArv(e.target.value)}
              placeholder="285,000"
              className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-[var(--line)] focus:outline-none focus:border-[var(--primary)]"
              data-testid="manual-arv-input"
              required
            />
          </div>
          <div className="text-[11px] text-[var(--ink-3)] mt-1">What it&apos;ll sell for once fixed</div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">
            Repair estimate
          </label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]">$</span>
            <input
              type="text"
              inputMode="numeric"
              value={repairs}
              onChange={(e) => setRepairs(e.target.value)}
              placeholder="35,000"
              className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-[var(--line)] focus:outline-none focus:border-[var(--primary)]"
              data-testid="manual-repairs-input"
            />
          </div>
          <div className="text-[11px] text-[var(--ink-3)] mt-1">Or your gut feel — you can edit later</div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">
            Asking price
          </label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]">$</span>
            <input
              type="text"
              inputMode="numeric"
              value={asking}
              onChange={(e) => setAsking(e.target.value)}
              placeholder="120,000"
              className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-[var(--line)] focus:outline-none focus:border-[var(--primary)]"
              data-testid="manual-asking-input"
            />
          </div>
          <div className="text-[11px] text-[var(--ink-3)] mt-1">What the seller wants (optional)</div>
        </div>
      </div>
      <button
        type="submit"
        className="btn-money w-full md:w-auto"
        disabled={submitting || !arv}
        data-testid="manual-details-submit"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {submitting ? "Running the math…" : "Run my analysis"}
      </button>
    </form>
  );
}
